import React, { useRef, useState } from "react";
import { apiClient } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Send, Loader2, Sparkles, Database } from "lucide-react";

const SUGGESTED_PROMPTS = [
  "Which project is delayed?",
  "How much cement stock is available?",
  "Show today's attendance summary",
  "Summarize the latest DPR",
  "Are there any safety incidents this week?",
  "What are the low-stock items right now?",
  "M25 mix design proportions (IS 10262)",
  "Rebar cover for slab as per IS 456",
];

export default function CivilChat() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm **Ask Setu Copilot** — your AI project intelligence assistant. I can answer questions using your live project data (projects, attendance, DPRs, inventory, safety) OR general civil engineering (IS/ACI/BS codes, materials, design). Try a suggestion below or ask anything." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sid, setSid] = useState(null);
  const [lastCtx, setLastCtx] = useState(null);
  const endRef = useRef(null);

  const send = async (customQ) => {
    const q = (customQ || input).trim();
    if (!q || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      const { data } = await apiClient().post("/ai/chat", { message: q, session_id: sid });
      setSid(data.session_id);
      setLastCtx(data.context_used || null);
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Copilot error");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 fade-in-up flex flex-col h-[calc(100vh-8rem)]" data-testid="chat-page">
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.25em] text-primary flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> AI Copilot</div>
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-1 tracking-tight" data-testid="copilot-title">Ask Setu Copilot</h1>
        <p className="text-sm text-muted-foreground mt-1">Connected to your projects, attendance, DPRs, inventory & safety data.</p>
      </div>

      {/* Suggested prompts */}
      {messages.length <= 1 && (
        <div className="flex flex-wrap gap-2" data-testid="suggested-prompts">
          {SUGGESTED_PROMPTS.map((p) => (
            <button key={p} onClick={() => send(p)} disabled={busy}
              className="mono text-[10px] uppercase tracking-widest border border-border bg-card hover:border-primary/50 hover:bg-muted px-3 py-1.5 transition disabled:opacity-50">
              {p}
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 border border-border bg-card p-5 overflow-y-auto space-y-3" data-testid="chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] border px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
              m.role === "user" ? "border-primary bg-primary/10" : "border-border bg-background"
            }`}>
              <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1">
                {m.role === "user" ? "you" : "setu copilot"}
              </div>
              {m.content}
            </div>
          </div>
        ))}
        {busy && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Analysing your data…</div>}
        <div ref={endRef} />
      </div>

      {lastCtx && (
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-3 flex-wrap" data-testid="context-strip">
          <span className="inline-flex items-center gap-1"><Database className="h-3 w-3 text-primary" /> Data used:</span>
          <span>{lastCtx.projects} projects</span>
          <span>·</span>
          <span>{lastCtx.attendance_today} punches today</span>
          <span>·</span>
          <span>{lastCtx.low_stock} low-stock items</span>
          <span>·</span>
          <span>{lastCtx.incidents} recent incidents</span>
        </div>
      )}

      <div className="border border-border bg-card p-3 flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          data-testid="chat-input"
          className="flex-1 bg-background border border-border px-3 py-3 text-base sm:text-sm focus:outline-none focus:border-primary" />
        <button onClick={() => send()} disabled={busy} data-testid="chat-send-btn"
          className="btn-sharp inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 disabled:opacity-50">
          <Send className="h-4 w-4" /> Send
        </button>
      </div>
    </div>
  );
}
